import React, { type ComponentProps, type ComponentType, type ReactNode } from 'react';

import clsx from 'clsx';
import { ContextMenu, useDialogIsOpen, useDialogOnNearestManager } from '../Dialog';
import { useComponentContext, useTranslationContext } from '../../context';
import {
  defaultChannelActionSet,
  useBaseChannelActionSetFilter,
} from './ChannelListItemActionButtons.defaults';
import { useSplitActionSet } from '../Chat/hooks/useSplitActionSet';
import { useChannelListItemContext } from './ChannelListItem';

export type ChannelListItemActionButtonsProps = ComponentProps<ComponentType>; // hack to allow empty props

interface ChannelListItemActionButtonsInterface {
  (props: ChannelListItemActionButtonsProps): ReactNode;
  getDialogId: (_: { channelId: string }) => string;
  displayName: string;
}

export const ChannelListItemActionButtons: ChannelListItemActionButtonsInterface = () => {
  const { ContextMenu: ContextMenuComponent = ContextMenu } = useComponentContext();
  const { t } = useTranslationContext();
  const { channel } = useChannelListItemContext();
  const [referenceElement, setReferenceElement] =
    React.useState<HTMLButtonElement | null>(null);
  const [isRestoringFocus, setIsRestoringFocus] = React.useState(false);
  const dialogId = ChannelListItemActionButtons.getDialogId({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    channelId: channel.id!,
  });
  const { dialog, dialogManager } = useDialogOnNearestManager({ id: dialogId });
  const dialogIsOpen = useDialogIsOpen(dialogId, dialogManager?.id);

  const closeContextMenu = React.useCallback(() => {
    setIsRestoringFocus(true);
    dialog?.close();

    requestAnimationFrame(() => {
      if (!referenceElement?.isConnected) {
        setIsRestoringFocus(false);
        return;
      }

      referenceElement.focus();

      requestAnimationFrame(() => {
        if (document.activeElement !== referenceElement) {
          setIsRestoringFocus(false);
        }
      });
    });
  }, [dialog, referenceElement]);

  const filteredActionSet = useBaseChannelActionSetFilter(defaultChannelActionSet);
  const { dropdownActionSet, quickActionSet, quickDropdownToggleAction } =
    useSplitActionSet(filteredActionSet);

  if (quickActionSet.length + dropdownActionSet.length === 0) {
    // no buttons to render, omit rendering wrapper
    return null;
  }

  return (
    <div
      className={clsx('str-chat__channel-list-item__action-buttons', {
        'str-chat__channel-list-item__action-buttons--active':
          dialogIsOpen || isRestoringFocus,
      })}
      data-testid='channel-list-item-action-buttons'
      onFocusCapture={() => {
        setIsRestoringFocus(false);
      }}
    >
      {quickDropdownToggleAction && dropdownActionSet.length > 0 && (
        <quickDropdownToggleAction.Component ref={setReferenceElement} />
      )}
      {quickActionSet.map(({ Component, type }) => (
        <Component key={type} />
      ))}
      <ContextMenuComponent
        aria-label={t('aria/Channel Actions')}
        className='str-chat__channel-list-item__action-buttons-context-menu'
        data-testid='channel-list-item-context-menu'
        dialogManagerId={dialogManager?.id}
        id={dialog.id}
        onClose={closeContextMenu}
        placement='bottom-start'
        referenceElement={referenceElement}
        tabIndex={-1}
        trapFocus
      >
        {dropdownActionSet.map(({ Component, type }) => (
          <Component key={type} />
        ))}
      </ContextMenuComponent>
    </div>
  );
};

ChannelListItemActionButtons.getDialogId = ({ channelId }) =>
  `channel-action-buttons-${channelId}`;

ChannelListItemActionButtons.displayName = 'ChannelListItemActionButtons';
